import {
  HMIKeyType,
  HMIParamsType,
  IHMI,
  IHMIParamsResponse,
  ISetHMIParams,
} from "@/core/models/hmi.model";
import {
  IPredictionRequest,
  IPredictionResponse,
} from "@/core/models/prediction.model";
import {
  IRecommendationOutput,
  IRecommendationPayload,
  IRecommendationRequest,
  IRecommendationResponse,
  IRecommendationResultParameter,
  IRecommendationHistory,
} from "@/core/models/recommendation.model";
import { HMI_UNITS } from "@/core/constants";
import { fixNumber } from "@/core/utils/decoder.utils";

export const HMIParamsTransformer = (
  response: IHMIParamsResponse[],
  { type, data: paramsData, checkEnable }: ISetHMIParams
): IHMI["params"] => {
  return response.reduce((acc, item) => {
    const {
      id,
      name,
      minimum,
      maximum,
      default: defaultValue,
      last_value: latestValue,
      input: enabled,
    } = item;
    const calcValue = paramsData
      ? fixNumber(paramsData[name])
      : type === "default"
      ? fixNumber(defaultValue)
      : type === "latest"
      ? fixNumber(latestValue)
      : null;
    const value = checkEnable ? (enabled ? calcValue : null) : calcValue;
    acc[name] = {
      id,
      value,
      min: minimum,
      max: maximum,
      enable: enabled,
      error: Number(value) < minimum || Number(value) > maximum,
      unit: HMI_UNITS[name],
    };
    return acc;
  }, {} as HMIParamsType);
};

export const predictionPayloadTransformer = (
  data: HMIParamsType,
  interval: number
): IPredictionRequest => ({
  interval,
  predictive_parameters: Object.values(data)
    .filter((param: any) => param.id !== 92) // exclude FR_X119_1
    .map((param: any) => ({
      parameter: param.id,
      value: Number(param.value),
    })),
});

export const predictionResultTransformer = (
  response: IPredictionResponse
): IHMI["predictionResult"] => ({
  id: response.id,
  carbon: response.carbon,
  metallization: response.metallization,
  productionRate: response.production_rate,
  errorRateMetal: response.error_rate_metal,
  errorRateCarbon: response.error_rate_carbon,
});

export const predictionHistoryTransformer = (
  response: IPredictionResponse[]
): IHMI["predictionHistory"] => {
  return response.map((prediction) => {
    let inputParameters: Partial<Record<HMIKeyType, number>> = {};
    prediction.input_parameters.forEach((param) => {
      inputParameters = {
        ...inputParameters,
        [param.parameter.name]: param.value,
      };
    });

    return {
      id: prediction.id,
      interval: prediction.interval,
      carbon: prediction.carbon,
      metallization: prediction.metallization,
      productionRate: prediction.production_rate,
      time: new Date(prediction.created_at),
      inputParameters: inputParameters as Record<HMIKeyType, number>,
      errorRateMetal: prediction.error_rate_metal,
      errorRateCarbon: prediction.error_rate_carbon,
    };
  });
};

export const recommendationPayloadTransformer = (
  paramsData: HMIParamsType,
  payload: IRecommendationPayload
): IRecommendationRequest => ({
  carbon: payload.carbon,
  metal: payload.metal,
  predictive_parameters: Object.values(paramsData)
    .filter((param) => param.value !== null && param.value !== undefined)
    .map((param) => ({
      parameter: param.id,
      value: Number(param.value),
    })),
});

const recommendationOutputsTransformer = (
  response: IRecommendationOutput[]
): IRecommendationResultParameter[] => {
  let filteredParams: Partial<Record<HMIKeyType, number>> = {};
  response.forEach((param) => {
    const key = param.parameter.name;
    const value = param.value;
    const currentValue = filteredParams[key];
    if (currentValue === undefined || currentValue < value) {
      filteredParams = { ...filteredParams, [key]: value };
    }
  });
  return Object.entries(filteredParams).map(([key, value]) => ({
    name: key as HMIKeyType,
    value: value as number,
  }));
};

export const recommendationResultTransformer = (
  response: IRecommendationResponse
): IHMI["recommendationResult"] => ({
  id: response.id,
  carbon: response.carbon,
  metallization: response.metallization,
  productionRate: response.production_rate,
  parameters: recommendationOutputsTransformer(response.output),
});

export const recommendationHistoryTransformer = (
  response: IRecommendationResponse[]
): IRecommendationHistory[] => {
  return response.map((item) => ({
    id: item.id,
    carbon: item.carbon,
    metallization: item.metallization,
    productionRate: item.production_rate,
    time: item.created_at,
    targetCarbon:
      typeof item.target_carbon === "object"
        ? item.target_carbon?.parsedValue ?? null
        : Number(item.target_carbon ?? 0) || null,
    targetMetal:
      typeof item.target_metal === "object"
        ? item.target_metal?.parsedValue ?? null
        : Number(item.target_metal ?? 0) || null,
    parameters:
      item.output?.map((param) => ({
        name: param.parameter.name,
        value: param.value,
      })) ?? [],
    inputParameters:
      item.input_parameters?.map((param) => ({
        name: param.parameter.name,
        value:
          typeof param.value === "object" && "parsedValue" in param.value
            ? (param.value as any).parsedValue
            : param.value,
      })) ?? [],
  }));
};
